import { useState, useRef, useMemo } from 'react';
import { Button, chakra, HStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Routes as AppRoutes } from '@view/routes';
import { BottomSheet, BottomSheetRef } from 'react-spring-bottom-sheet';
import styled from '@emotion/styled';
import BEIcon from '@molecules/BEIcon';
import { BELoadingButton } from '@molecules/Button/Button';
import Text from '@molecules/Text';
import { useStore } from '@data/other/withStore';
import { ShoppingCartProps } from '@view/components/Cards/ShoppingCart';
import ShoppingCartCard from '@view/components/Cards/ShoppingCart/ShoppingCartCard';
import { observer } from 'mobx-react';

import 'react-spring-bottom-sheet/dist/style.css';
import useFormat from '@business/utils/hooks/useFormat';

const StyledShoppingCartFooter = styled(HStack)`
  position: fixed;
  bottom: 0px;
  left: 0px;
  background-color: white;
  z-index: 100;

  &.fadeOut{
    opacity: 0;
    height: 0;
    transition: height 0.3s, opacity 0.3s;
  }
  &.fadeIn{
    opacity: 1;
    height: 80px;
  }
`;
const StyledViewSummaryButton = chakra(Button, {
  baseStyle: {
    width: '100%',
    height: '40px',
    borderRadius: '16px 16px 0 0',
    fontSize: '14px',
    fontWeight: 500,
  },
});

const MobileShoppingCart = (props: ShoppingCartProps) => {
  const sheetRef = useRef<BottomSheetRef>(null);
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  const confirmButtonText = useMemo(() => {
    if (props.path === AppRoutes.Accommodations) {
      return t('book-now');
    } else if (props.path === AppRoutes.Payment) {
      return t('pay-confirm');
    } else {
      return t('continue');
    }
  }, [props.path, t]);

  const {
    property: { cart },
  } = useStore();
  const { currency, date: formatDate } = useFormat();

  const handleOnOpen = () => {
    sheetRef.current &&
      sheetRef.current.snapTo(({ snapPoints, lastSnap, maxHeight }) => {
        const newSnap =
          lastSnap === maxHeight
            ? Math.min(...snapPoints)
            : Math.max(...snapPoints);
        return newSnap;
      });
  };

  return (
    <>
      <BottomSheet
        open
        ref={sheetRef}
        defaultSnap={({ snapPoints }) => Math.min(...snapPoints)}
        snapPoints={({ maxHeight }) => [120, maxHeight]}
        blocking={false}
        scrollLocking={false}
        skipInitialTransition={true}
        onSpringStart={() => {
          setExpanded(false);
        }}
        onSpringEnd={() => {
          if (sheetRef.current?.height === 120) {
            setExpanded(false);
          } else {
            setExpanded(true);
          }
        }}
        header={
          <StyledViewSummaryButton colorScheme="primary" onClick={handleOnOpen}>
            View Summary ({cart.items.length}){' '}
            <BEIcon name={expanded ? 'angle-down' : 'angle-up'} />
          </StyledViewSummaryButton>
        }
      >
        <ShoppingCartCard {...props} onCancel={handleOnOpen} />
      </BottomSheet>
      <StyledShoppingCartFooter
        w="100%"
        p="16px"
        justifyContent="space-between"
        className={expanded ? 'fadeOut' : 'fadeIn'}
      >
        <div>
          <Text type="h1">{currency(cart.totals?.total)}</Text>
          <Text>
            {formatDate(cart.checkIn, 'D MMM')}
            &nbsp;-&nbsp;
            {formatDate(cart.checkOut, 'D MMM')}
          </Text>
        </div>
        <BELoadingButton
          color="primary"
          size="lg"
          isDisabled={!cart.totals}
          onClick={props.onOk}
        >
          {confirmButtonText}
        </BELoadingButton>
      </StyledShoppingCartFooter>
    </>
  );
};

export default observer(MobileShoppingCart);
